import { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import {
  listings as allListings,
  cities,
  districtsByCity,
  zoningOptions,
  deedOptions,
} from "../../mocks/listings";
import type { Listing } from "../../mocks/listings";

function formatPrice(price: number) {
  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0,
  }).format(price);
}

function ListingCard({ listing }: { listing: Listing }) {
  return (
    <Link
      to={`/ilan/${listing.id}`}
      className="group flex flex-col sm:flex-row bg-white rounded-xl overflow-hidden border border-surface-100 hover:border-surface-200 hover:shadow-medium transition-all"
    >
      <div className="relative w-full sm:w-[240px] md:w-[280px] shrink-0 h-48 sm:h-auto overflow-hidden">
        <img
          src={listing.photos[0]}
          alt={listing.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-2.5 left-2.5 flex flex-col gap-1.5">
          {listing.featured && (
            <span className="px-2 py-1 bg-primary-500 text-white text-[11px] font-bold rounded-md">
              Öne Çıkan
            </span>
          )}
          {listing.user_type === "Emlak Ofisi" && (
            <span className="px-2 py-1 bg-white/90 backdrop-blur-sm text-[11px] font-semibold text-primary-600 rounded-md">
              Emlak Ofisi
            </span>
          )}
        </div>
        <div className="absolute bottom-2.5 right-2.5 px-2 py-1 bg-surface-900/70 backdrop-blur-sm text-white text-[11px] font-medium rounded-md">
          {listing.photos.length} foto
        </div>
      </div>
      <div className="flex-1 p-4 md:p-5 flex flex-col">
        <div className="flex items-start justify-between gap-3 mb-2">
          <h3 className="text-sm md:text-base font-semibold text-surface-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
            {listing.title}
          </h3>
        </div>

        <div className="flex items-center gap-2 text-xs text-surface-500 mb-2.5">
          <div className="w-3.5 h-3.5 flex items-center justify-center">
            <i className="ri-map-pin-line" />
          </div>
          {listing.city}, {listing.district}
        </div>

        <p className="text-xs md:text-sm text-surface-500 leading-relaxed line-clamp-2 mb-3 flex-1">
          {listing.description}
        </p>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-surface-500 mb-3">
          <span className="flex items-center gap-1.5">
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-shape-line" />
            </div>
            {listing.area_m2.toLocaleString("tr-TR")} m²
          </span>
          <span className="flex items-center gap-1.5">
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-file-list-line" />
            </div>
            {listing.zoning_status}
          </span>
          <span className="flex items-center gap-1.5">
            <div className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-shield-check-line" />
            </div>
            {listing.deed_status}
          </span>
          {listing.swap && (
            <span className="flex items-center gap-1.5 text-accent-600">
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-exchange-line" />
              </div>
              Takas Olur
            </span>
          )}
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-surface-100 mt-auto">
          <span className="text-base md:text-lg font-bold text-primary-600">
            {formatPrice(listing.price)}
          </span>
          <span className="text-xs text-surface-400">{listing.user_name}</span>
        </div>
      </div>
    </Link>
  );
}

function FilterPanel({
  filters,
  onFilterChange,
  resultCount,
  mobile,
  onClose,
}: {
  filters: FilterState;
  onFilterChange: (key: string, value: string | boolean) => void;
  resultCount: number;
  mobile?: boolean;
  onClose?: () => void;
}) {
  const [selectedCity, setSelectedCity] = useState(filters.city);
  const districts = selectedCity
    ? districtsByCity[selectedCity] || []
    : [];

  const handleCityChange = (city: string) => {
    setSelectedCity(city);
    onFilterChange("city", city);
    onFilterChange("district", "");
  };

  const clearFilters = () => {
    onFilterChange("city", "");
    onFilterChange("district", "");
    onFilterChange("zoning", "");
    onFilterChange("deed", "");
    onFilterChange("minPrice", "");
    onFilterChange("maxPrice", "");
    onFilterChange("minArea", "");
    onFilterChange("maxArea", "");
    onFilterChange("swap", false);
    setSelectedCity("");
  };

  const hasActiveFilters =
    filters.city ||
    filters.district ||
    filters.zoning ||
    filters.deed ||
    filters.minPrice ||
    filters.maxPrice ||
    filters.minArea ||
    filters.maxArea ||
    filters.swap;

  return (
    <div
      className={`${mobile ? "" : "w-[280px] lg:w-[300px] shrink-0 sticky top-24 self-start"}`}
    >
      {mobile && (
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-surface-100">
          <h3 className="text-base font-bold text-surface-900">Filtreler</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-surface-100 text-surface-500 hover:bg-surface-200 transition-colors"
          >
            <i className="ri-close-line" />
          </button>
        </div>
      )}

      <div className="bg-white rounded-xl border border-surface-200 p-5 space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-surface-900">Filtreler</h3>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-xs text-primary-500 hover:text-primary-600 font-medium transition-colors"
            >
              Temizle
            </button>
          )}
        </div>

        {/* City */}
        <div>
          <label className="text-xs font-medium text-surface-500 mb-2 block">
            Şehir
          </label>
          <div className="relative">
            <select
              value={filters.city}
              onChange={(e) => handleCityChange(e.target.value)}
              className="w-full pl-3 pr-8 py-2.5 bg-surface-50 border border-surface-200 rounded-lg text-sm text-surface-700 appearance-none focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
            >
              <option value="">Tüm Şehirler</option>
              {cities.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-surface-400 pointer-events-none">
              <i className="ri-arrow-down-s-line" />
            </div>
          </div>
        </div>

        {/* District */}
        <div>
          <label className="text-xs font-medium text-surface-500 mb-2 block">
            İlçe
          </label>
          <div className="relative">
            <select
              value={filters.district}
              onChange={(e) => onFilterChange("district", e.target.value)}
              disabled={!districts.length}
              className="w-full pl-3 pr-8 py-2.5 bg-surface-50 border border-surface-200 rounded-lg text-sm text-surface-700 appearance-none focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300 disabled:opacity-50"
            >
              <option value="">
                {districts.length ? "Tüm İlçeler" : "Önce Şehir Seçin"}
              </option>
              {districts.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
            <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-surface-400 pointer-events-none">
              <i className="ri-arrow-down-s-line" />
            </div>
          </div>
        </div>

        {/* Price */}
        <div>
          <label className="text-xs font-medium text-surface-500 mb-2 block">
            Fiyat Aralığı
          </label>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-surface-400">
                ₺
              </span>
              <input
                type="number"
                placeholder="Min"
                value={filters.minPrice}
                onChange={(e) => onFilterChange("minPrice", e.target.value)}
                className="w-full pl-6 pr-2 py-2 bg-surface-50 border border-surface-200 rounded-lg text-sm text-surface-700 placeholder:text-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
              />
            </div>
            <span className="text-surface-300 text-sm">-</span>
            <div className="relative flex-1">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-surface-400">
                ₺
              </span>
              <input
                type="number"
                placeholder="Maks"
                value={filters.maxPrice}
                onChange={(e) => onFilterChange("maxPrice", e.target.value)}
                className="w-full pl-6 pr-2 py-2 bg-surface-50 border border-surface-200 rounded-lg text-sm text-surface-700 placeholder:text-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
              />
            </div>
          </div>
        </div>

        {/* Area */}
        <div>
          <label className="text-xs font-medium text-surface-500 mb-2 block">
            Alan (m²)
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              placeholder="Min"
              value={filters.minArea}
              onChange={(e) => onFilterChange("minArea", e.target.value)}
              className="w-full px-2.5 py-2 bg-surface-50 border border-surface-200 rounded-lg text-sm text-surface-700 placeholder:text-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
            />
            <span className="text-surface-300 text-sm">-</span>
            <input
              type="number"
              placeholder="Maks"
              value={filters.maxArea}
              onChange={(e) => onFilterChange("maxArea", e.target.value)}
              className="w-full px-2.5 py-2 bg-surface-50 border border-surface-200 rounded-lg text-sm text-surface-700 placeholder:text-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
            />
          </div>
        </div>

        {/* Zoning */}
        <div>
          <label className="text-xs font-medium text-surface-500 mb-2 block">
            İmar Durumu
          </label>
          <div className="relative">
            <select
              value={filters.zoning}
              onChange={(e) => onFilterChange("zoning", e.target.value)}
              className="w-full pl-3 pr-8 py-2.5 bg-surface-50 border border-surface-200 rounded-lg text-sm text-surface-700 appearance-none focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
            >
              <option value="">Tümü</option>
              {zoningOptions.map((z) => (
                <option key={z} value={z}>
                  {z}
                </option>
              ))}
            </select>
            <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-surface-400 pointer-events-none">
              <i className="ri-arrow-down-s-line" />
            </div>
          </div>
        </div>

        {/* Deed */}
        <div>
          <label className="text-xs font-medium text-surface-500 mb-2 block">
            Tapu Durumu
          </label>
          <div className="relative">
            <select
              value={filters.deed}
              onChange={(e) => onFilterChange("deed", e.target.value)}
              className="w-full pl-3 pr-8 py-2.5 bg-surface-50 border border-surface-200 rounded-lg text-sm text-surface-700 appearance-none focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
            >
              <option value="">Tümü</option>
              {deedOptions.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
            <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-surface-400 pointer-events-none">
              <i className="ri-arrow-down-s-line" />
            </div>
          </div>
        </div>

        {/* Swap */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => onFilterChange("swap", !filters.swap)}
            className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
              filters.swap
                ? "bg-primary-500 border-primary-500 text-white"
                : "border-surface-300 bg-white"
            }`}
          >
            {filters.swap && <i className="ri-check-line text-xs" />}
          </button>
          <label className="text-sm text-surface-700 cursor-pointer select-none">
            Sadece Takas Olur İlanlar
          </label>
        </div>

        <div className="pt-2 border-t border-surface-100">
          <p className="text-xs text-surface-500 text-center">
            {resultCount} ilan bulundu
          </p>
        </div>
      </div>
    </div>
  );
}

interface FilterState {
  city: string;
  district: string;
  minPrice: string;
  maxPrice: string;
  minArea: string;
  maxArea: string;
  zoning: string;
  deed: string;
  swap: boolean;
}

function useFilters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<FilterState>({
    city: searchParams.get("city") || "",
    district: searchParams.get("district") || "",
    minPrice: searchParams.get("minPrice") || "",
    maxPrice: searchParams.get("maxPrice") || "",
    minArea: searchParams.get("minArea") || "",
    maxArea: searchParams.get("maxArea") || "",
    zoning: searchParams.get("zoning") || "",
    deed: searchParams.get("deed") || "",
    swap: searchParams.get("swap") === "true",
  });

  const handleFilterChange = (key: string, value: string | boolean) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const filteredListings = useMemo(() => {
    return allListings.filter((listing) => {
      if (filters.city && listing.city !== filters.city) return false;
      if (filters.district && listing.district !== filters.district)
        return false;
      if (
        filters.minPrice &&
        listing.price < parseInt(filters.minPrice, 10)
      )
        return false;
      if (
        filters.maxPrice &&
        listing.price > parseInt(filters.maxPrice, 10)
      )
        return false;
      if (
        filters.minArea &&
        listing.area_m2 < parseInt(filters.minArea, 10)
      )
        return false;
      if (
        filters.maxArea &&
        listing.area_m2 > parseInt(filters.maxArea, 10)
      )
        return false;
      if (filters.zoning && listing.zoning_status !== filters.zoning)
        return false;
      if (filters.deed && listing.deed_status !== filters.deed) return false;
      if (filters.swap && !listing.swap) return false;
      return true;
    });
  }, [filters]);

  return { filters, handleFilterChange, filteredListings };
}

export default function ListingPage() {
  const { filters, handleFilterChange, filteredListings } = useFilters();
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [sortBy, setSortBy] = useState("newest");

  const sortedListings = useMemo(() => {
    const sorted = [...filteredListings];
    switch (sortBy) {
      case "price-asc":
        sorted.sort((a, b) => a.price - b.price);
        break;
      case "price-desc":
        sorted.sort((a, b) => b.price - a.price);
        break;
      case "area-asc":
        sorted.sort((a, b) => a.area_m2 - b.area_m2);
        break;
      case "area-desc":
        sorted.sort((a, b) => b.area_m2 - a.area_m2);
        break;
      default:
        sorted.sort(
          (a, b) =>
            new Date(b.created_at).getTime() -
            new Date(a.created_at).getTime(),
        );
    }
    return sorted;
  }, [filteredListings, sortBy]);

  return (
    <div className="min-h-screen bg-surface-50">
      <Navbar />

      <main className="pt-20 md:pt-24 pb-16">
        <div className="w-full px-4 md:px-6 lg:px-10">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-surface-900">
                  Arsa İlanları
                </h1>
                <p className="text-sm text-surface-500 mt-1">
                  {filteredListings.length} ilan listeleniyor
                </p>
              </div>
              <div className="flex items-center gap-3">
                {/* Mobile Filter Button */}
                <button
                  onClick={() => setMobileFilterOpen(true)}
                  className="md:hidden inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-surface-200 rounded-lg text-sm font-medium text-surface-700 hover:border-surface-300 transition-colors"
                >
                  <i className="ri-filter-3-line" />
                  Filtrele
                </button>

                {/* Sort */}
                <div className="relative">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="pl-3 pr-8 py-2.5 bg-white border border-surface-200 rounded-lg text-sm text-surface-700 appearance-none focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300"
                  >
                    <option value="newest">En Yeni</option>
                    <option value="price-asc">Fiyat: Düşükten Yükseğe</option>
                    <option value="price-desc">
                      Fiyat: Yüksekten Düşüğe
                    </option>
                    <option value="area-asc">Alan: Küçükten Büyüğe</option>
                    <option value="area-desc">Alan: Büyükten Küçüğe</option>
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-surface-400 pointer-events-none">
                    <i className="ri-arrow-down-s-line" />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-6">
              {/* Desktop Filters */}
              <div className="hidden md:block">
                <FilterPanel
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  resultCount={filteredListings.length}
                />
              </div>

              {/* Listings */}
              <div className="flex-1 min-w-0">
                {sortedListings.length === 0 ? (
                  <div className="bg-white rounded-xl border border-surface-200 p-12 text-center">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-surface-100 text-surface-400 mx-auto mb-4">
                      <i className="ri-search-line text-2xl" />
                    </div>
                    <h3 className="text-base font-semibold text-surface-900 mb-2">
                      İlan Bulunamadı
                    </h3>
                    <p className="text-sm text-surface-500 max-w-xs mx-auto">
                      Seçtiğiniz filtrelere uygun ilan bulunmuyor. Filtreleri
                      genişletmeyi deneyin.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {sortedListings.map((listing) => (
                      <ListingCard key={listing.id} listing={listing} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Mobile Filter Drawer */}
      {mobileFilterOpen && (
        <>
          <div
            className="fixed inset-0 bg-surface-900/40 backdrop-blur-sm z-40"
            onClick={() => setMobileFilterOpen(false)}
          />
          <div className="fixed top-0 right-0 bottom-0 w-[320px] max-w-[85vw] bg-white z-50 overflow-y-auto p-4">
            <FilterPanel
              filters={filters}
              onFilterChange={handleFilterChange}
              resultCount={filteredListings.length}
              mobile
              onClose={() => setMobileFilterOpen(false)}
            />
          </div>
        </>
      )}

      <Footer />
    </div>
  );
}